import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');
// Prevent duplicate token exchange (React Strict Mode double mount / callback re-entry)
let isProcessingSalesforceTokenExchange = false;

export const getSalesforceCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    const salesforceConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
        mc.mcId?.toString() === mcId?.toString()) &&
      mc.mcName === "Salesforce"
    );
    if (!salesforceConnector) {
      throw new Error('Salesforce master connector not found');
    }

    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error(`Failed to fetch connector parameters: ${paramResponse.status}`);
    }
    const connectorParams = await paramResponse.json();
    const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;
    if (!clientId || clientId.trim() === '') {
      throw new Error('Client ID is required but not found in connector parameters. Please configure it in settings.');
    }

    if (clientId.trim().length < 50) {
      throw new Error('Invalid client ID');
    }

    localStorage.setItem('PendingSalesforceConnectorId', connectorId);
    localStorage.setItem('ConnectorType', 'Salesforce');

    const authEndpoint = salesforceConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Salesforce Authentication URL is missing from Master Connector data');
    }
    const salesforceAuthUrl = `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}client_id=${clientId}&redirect_uri=${process.env.REACT_APP_OAUTH_REDIRECT_URI}&response_type=code&scope=full api refresh_token&state=${connectorId}`;

    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      const tokenResponse = await exchangeSalesforceAuthCodeForToken(
        storedAuthCode,
        clientId,
        mcId
      );

      if (tokenResponse == null) {
        return null;
      }

      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingSalesforceConnectorId');
      localStorage.removeItem('ConnectorType');

      return tokenResponse;
    } else {
      window.location.href = salesforceAuthUrl;
      return null;
    }
  } catch (error) {
    console.error('Error getting Salesforce credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for Salesforce token
export const exchangeSalesforceAuthCodeForToken = async (authCode, clientId, mcId) => {
  if (isProcessingSalesforceTokenExchange) {
    console.warn('Salesforce token exchange already in progress; skipping duplicate to avoid auth code reuse / invalid_grant');
    return null;
  }
  isProcessingSalesforceTokenExchange = true;

  try {


    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/salesforce/authToken`;

    const response = await fetch(`${tokenUrl}?code=${encodeURIComponent(authCode)}&mcId=${encodeURIComponent(mcId)}`, {

      method: 'POST',

      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();


    // Get the connector ID that was stored before redirect

    const connectorId = localStorage.getItem('PendingSalesforceConnectorId');

    if (connectorId) {

      // Update the connector with token data

      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;

      try {

        // First, get the current connector data

        const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {

          headers: {

            'Accept': 'application/json',

            'Authorization': `Bearer ${token}`,

          }
        });

        if (!connectorResponse.ok) {

          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);

        }

        const connectorData = await connectorResponse.json();

        // Calculate token expiry dates (Salesforce provides issued_at instead of expires_in)

        // Typically Salesforce tokens are valid for 2 hours (7200 seconds)

        const TOKEN_VALIDITY_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

        // Calculate the 14-day expiry date for refresh token

        const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

        const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();

        // Convert issued_at from string to number if necessary

        const issuedAt = typeof tokenData.issued_at === 'string'

          ? parseInt(tokenData.issued_at)

          : tokenData.issued_at;

        // Calculate token expiry time based on issued_at

        const tokenExpiry = new Date(issuedAt + TOKEN_VALIDITY_MS).toISOString();

        // Prepare updated connector data

        const updatedConnectorData = {

          ...connectorData,

          token: tokenData.access_token,

          refreshToken: tokenData.refresh_token,

          tokenExpiry: tokenExpiry,

          refreshUrl: connectorData.refreshUrl,

          refreshTokenExpiry: refreshTokenExpiry,

          idToken: tokenData.id_token,

          instanceUrl: tokenData.instance_url,

          signature: tokenData.signature,

          tokenType: tokenData.token_type,

          status: 'Connected',

          connectedTime: new Date().toISOString()

        };

        // Make the PUT request to update the connector

        const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {

          method: 'PUT',

          headers: {

            'Content-Type': 'application/json',

            'Accept': 'application/json',

            'Authorization': `Bearer ${token}`,

          },

          body: JSON.stringify(updatedConnectorData)

        });

        if (!updateResponse.ok) {

          const errorText = await updateResponse.text();

          console.error('Update connector error:', errorText);

          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }
        console.log('Connector updated successfully with tokens');
      } catch (error) {

        console.error('Error updating connector with tokens:', error);

        // Don't throw here - we still want to return the token data

      }

    }

    return tokenData;

  } catch (error) {

    console.error('Token exchange error:', error);

    throw error;

  } finally {
    isProcessingSalesforceTokenExchange = false;
  }

};
